import axiosRetry from 'axios-retry';
import type { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { Store } from 'vuex';

import { addBreadcrumb, captureException } from '../plugins/sentry';
import {
  canRecoverMissingToken,
  isMissingTokenResponse,
  recoverMissingToken,
  replayInternalUnauthorized,
  shouldRefreshAndReplay,
} from './sessionRecovery';
import TokenService from './token';
import { isTokenRefreshError } from './tokenRefreshError';
import { getCsrf } from './util';

import { SERVER_TIMESTAMP_HEADER, AUTHORIZATION } from '../constants/headers';
import EventNames from '../models/eventNames';

import eventBus from '../helpers/eventBus';
import { getCorrelationId } from './correlation';
import { isStatusUnauthorized } from './error';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Opt out of the same-URL request dedup (the interceptor aborts an
     * in-flight request when a newer one targets the same URL). Set this for
     * endpoints where a retry must not cancel the original request — e.g. Apple
     * Pay merchant validation, where an aborted request surfaces as a status 0
     * / InvalidAccessError on an already-closed Apple Pay session.
     */
    skipRequestDedup?: boolean;
    /**
     * Reject without dispatching `error/setError`. For callers that handle
     * every failure themselves (e.g. the fail-open nSure pre-auth check), where
     * the shared error state would route the funnel to its error step.
     */
    skipGlobalErrorHandler?: boolean;
  }
}

interface StoreState {
  error?: {
    status?: number | string;
    redirectLink?: string | null;
  };
}

interface StoreGetters {
  'error/isUnauthorized': boolean;
}

interface StoreMutations {
  setServerTimeDiff: (payload: number) => void;
}

interface StoreActions {
  'error/setError': (error: unknown) => void;
}

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: Date;
  };
}

let instance: AxiosInstance | null = null;

export const initAPI = (axios: typeof import('axios').default, store: Store<StoreState> & { getters: StoreGetters; commit: (mutation: string, payload?: unknown) => void; dispatch: (action: string, payload?: unknown) => Promise<unknown> }): AxiosInstance => {
  if (instance !== null) {
    return instance;
  }

  instance = axios.create();

  axiosRetry(instance, {
    retries: 1,
    retryDelay: retryCount => retryCount * 500,
    shouldResetTimeout: true,
  });

  const cancelBucket: Record<string, AbortController> = {};

  const releaseCancelToken = (config?: ExtendedAxiosRequestConfig): void => {
    const url = config?.url;

    if (url && cancelBucket[url]?.signal === config.signal) {
      delete cancelBucket[url];
    }
  };

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> => {
      const extendedConfig = config as ExtendedAxiosRequestConfig;
      extendedConfig.timeout = extendedConfig.timeout || 30000; // timeout request

      if (!extendedConfig.headers) {
        extendedConfig.headers = {} as InternalAxiosRequestConfig['headers'];
      }
      extendedConfig.headers['Content-Type'] = 'application/json';

      if ('error' in store.state) {
        extendedConfig.headers['X-Correlation-ID'] = getCorrelationId();
      }

      if (TokenService.hasToken()) {
        extendedConfig.headers[AUTHORIZATION] = `Bearer ${TokenService.getToken()}`;
      }

      extendedConfig.metadata = { startTime: new Date() };

      const url = extendedConfig.url || '';
      // Requests may opt out of same-URL dedup via `skipRequestDedup`, so a
      // retry does not abort the original in-flight request (see flag docs).
      if (!extendedConfig.skipRequestDedup) {
        if (cancelBucket[url]) {
          // XXX: cancel request for current url
          cancelBucket[url].abort();
        }

        const abortController = new AbortController();
        extendedConfig.signal = abortController.signal;

        cancelBucket[url] = abortController;
      }

      if (extendedConfig.method !== 'get' && url.indexOf('/') === 0) {
        // Add _csrf token header for internal yii2 calls
        const _csrf = getCsrf();
        if (_csrf !== null) {
          extendedConfig.headers['X-CSRF-TOKEN'] = _csrf;
        }
      }

      return extendedConfig;
    },
    (error: unknown) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response: AxiosResponse<unknown> & { config: ExtendedAxiosRequestConfig }) => {
      releaseCancelToken(response.config);

      if (typeof response.headers[SERVER_TIMESTAMP_HEADER] !== 'undefined') {
        const endTime = new Date();
        const startTime = response.config.metadata?.startTime?.getTime() || endTime.getTime();
        const responseDuration = endTime.getTime() - startTime;
        const serverTime = Number(response.headers[SERVER_TIMESTAMP_HEADER]) * 1000; // ms

        store.commit(
          'setServerTimeDiff',
          serverTime + responseDuration - Date.now(),
        );
      }

      return response;
    },
    (error: AxiosError & { config?: ExtendedAxiosRequestConfig }) => {
      releaseCancelToken(error.config);

      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      const { config, response } = error;
      if (response === undefined) {
        return Promise.reject(error);
      }

      if (isMissingTokenResponse(response.data)) {
        if (canRecoverMissingToken(config) && instance) {
          // The refresh token exists, so the missing JWT is recoverable: refresh
          // and replay the request once instead of logging the user out.
          return recoverMissingToken(instance, config).catch((recoveryError: unknown) => {
            // A definitive rejection inside requestNewToken already emitted
            // SESSION_TIMEOUT; a transient refresh failure must not log out.
            // requestNewToken also owns the decision to report, so a refresh failure
            // only leaves a breadcrumb here instead of a second event.
            if (isTokenRefreshError(recoveryError)) {
              addBreadcrumb({
                message: 'missing-token session recovery failed',
                data: {
                  reason: recoveryError.reason,
                  httpStatus: recoveryError.httpStatus ?? null,
                },
              });
            } else {
              captureException({
                error: recoveryError,
                extra: {
                  errorExtraMessage: 'missing-token session recovery failed',
                },
              });
            }

            return Promise.reject(error);
          });
        }

        eventBus.emit(EventNames.SESSION_TIMEOUT);
        return Promise.reject(error);
      }

      const requestUrl = config?.url || '';

      // XXX: Need to return wallet-service errors (PD-13274)
      if (requestUrl.indexOf('/public/web-wallets/') !== -1) {
        return Promise.reject(error);
      }

      const isExternalRequest = !requestUrl.includes('/jwt/refresh') && !requestUrl.startsWith('/');

      const buildUnauthorizedExtra = (): Record<string, unknown> => {
        const extra: Record<string, unknown> = {
          cookieEnabled: navigator.cookieEnabled,
          hasJwt: TokenService.hasToken(),
          hasRefreshToken: TokenService.hasRefreshToken(),
        };

        const { config: eConfig, response: eResponse } = error;
        if (eConfig) {
          extra.request = { url: eConfig.url, method: eConfig.method };
        }
        if (eResponse) {
          extra.responseStatus = eResponse.status;
        }

        return extra;
      };

      const refreshAndReplay = (
        requestConfig: ExtendedAxiosRequestConfig,
        extra: Record<string, unknown>,
      ): Promise<AxiosResponse<unknown>> => TokenService.requestNewToken(requestUrl)
        .then(({ jwt }: { jwt: string }) => {
          if (!instance) {
            throw new Error('Axios instance is not initialized');
          }
          if (!requestConfig.headers) {
            requestConfig.headers = {} as InternalAxiosRequestConfig['headers'];
          }
          requestConfig.headers[AUTHORIZATION] = `Bearer ${jwt}`;

          return instance.request(requestConfig);
        })
        .catch((e: unknown) => {
          // A failed refresh hands callers the original 401, like sessionRecovery
          // does; the wrapper is the token service's to report, not theirs.
          if (isTokenRefreshError(e)) {
            throw error;
          }
          captureException({ error: e, extra });
          throw e;
        });

      if (config?.skipGlobalErrorHandler) {
        // No shared error state and no logout, but an expired JWT is still refreshed once and the request replayed.
        if (isStatusUnauthorized(response.status) && isExternalRequest && shouldRefreshAndReplay(config, error)) {
          return refreshAndReplay(config, buildUnauthorizedExtra());
        }

        return Promise.reject(error);
      }

      store.dispatch('error/setError', error);

      if (store.getters['error/isUnauthorized']) {
        const extra = buildUnauthorizedExtra();

        if (isExternalRequest && config) {
          if (!shouldRefreshAndReplay(config, error)) {
            return Promise.reject(error);
          }

          return refreshAndReplay(config, extra);
        }

        const internalReplay = replayInternalUnauthorized(instance, config, requestUrl);
        if (internalReplay !== null) {
          return internalReplay;
        }

        captureException({ error, extra });

        eventBus.emit(EventNames.SESSION_TIMEOUT);
      }

      Object.assign(error, error.response);
      return Promise.reject(error);
    },
  );

  return instance;
};

export default (): AxiosInstance => {
  if (instance === null) {
    throw new Error('Please create axios instance!');
  }

  return instance;
};
